import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search as SearchIcon, X } from "lucide-react";
import { getTeams, search, api } from "../lib/api";
import { useLeague } from "../context/LeagueContext";
import { useSortByTeam } from "../lib/SortByTeamContext";
import { useAuth } from "../lib/AuthContext";
import { TeamBadge } from "./TeamBadge";
import OwnerQuickAdd from "./OwnerQuickAdd";
import { TEAM_INFO } from "../lib/teamMap";
import { useGameNav } from "../context/GameNavContext";

// Static, searchable navigation index (pages + PWHL/Insights subtabs).
const NAV_TARGETS = [
    { label: "Home", to: "/", kw: "landing" },
    { label: "PWHL", to: "/teams", kw: "teams hub" },
    { label: "Pucks", to: "/packs", kw: "cards collection" },
    { label: "Insights", to: "/records", kw: "records" },
    { label: "News", to: "/news" },
    { label: "Videos", to: "/videos" },
    { label: "Roadmap", to: "/roadmap" },
    { label: "Standings", to: "/teams?tab=standings", kw: "pwhl table" },
    { label: "Schedule", to: "/teams?tab=schedule", kw: "games fixtures" },
    { label: "Leaderboards", to: "/teams?tab=leaderboards", kw: "leaders stats" },
    { label: "Contracts", to: "/teams?tab=contracts" },
    { label: "Draft", to: "/teams?tab=draft" },
    { label: "Movement", to: "/teams?tab=transfers", kw: "transfers signings" },
    { label: "Trades", to: "/teams?tab=trades" },
    { label: "Awards", to: "/teams?tab=awards", kw: "trophies honours" },
    { label: "Playstyles", to: "/playstyles" },
    { label: "Demographics", to: "/demographics", kw: "nationality age" },
    { label: "Milestones", to: "/milestones", kw: "records" },
    { label: "Chemistry", to: "/chemistry", kw: "lines pairs chemistry duos trios combinations" },
    { label: "Rankings", to: "/rankings", kw: "ratings" },
    { label: "NCAA", to: "/ncaa", kw: "college leagues" },
    { label: "Olympics", to: "/olympics" },
    { label: "Draft Board", to: "/draft" },
];

const ADMIN_TARGETS = [
    { label: "Change League", to: "/admin?tab=players", kw: "players find change league" },
    { label: "Coaches", to: "/admin?tab=coaches" },
    { label: "Rosters & Lineups", to: "/admin?tab=rosters" },
    { label: "Playstyles", to: "/admin?tab=playstyles" },
    { label: "Roadmap", to: "/admin?tab=roadmap" },
    { label: "Studio", to: "/admin?tab=studio" },
    { label: "Seasons", to: "/admin?tab=seasons" },
    { label: "Season Calendar", to: "/admin?tab=season_calendar", kw: "calendar schedule events" },
    { label: "Accounts", to: "/admin?tab=accounts" },
    { label: "Settings", to: "/admin?tab=settings" },
];

const NHL_NAV = [
    { label: "Schedule", to: "/nhl/schedule" }, { label: "Standings", to: "/nhl/standings" },
    { label: "Leaderboards", to: "/nhl/leaderboards", kw: "leaders stats" }, { label: "Chemistry", to: "/nhl/chemistry" },
    { label: "Rosters", to: "/nhl/rosters" }, { label: "Lineups", to: "/nhl/lineups" },
];
const nhlSearch = (q) => api.get("/nhl/search", { params: { q } }).then((r) => r.data);
const NhlLogo = ({ url }) => (url ? <img src={url} alt="" className="h-4 w-4 object-contain shrink-0" /> : <span className="h-4 w-4 rounded-sm bg-white/10 shrink-0" />);

export default function GlobalSearch({ compact = false, bar = false, dropUp = false, autoFocus = false, focusToken = 0, onNavigate, onQueryChange, testId = "global-search", resultsBar = false }) {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const { hasAdminAccess, isOwner } = useAuth();
    const navigate = useNavigate();
    const boxRef = useRef(null);
    const inputRef = useRef(null);
    // Player-pick mode (Leaderboards Game Logs): search becomes players-only,
    // highlights purple, auto-opens/focuses, and picks instead of navigating.
    const { pick } = useGameNav();
    const { league } = useLeague();
    const { setNhlSurfTeam } = useSortByTeam();
    const playerMode = !!pick?.active;
    // Search is scoped to the league you're on (NHL players/teams on NHL, PWHL on PWHL).
    const isNhl = league === "nhl" && !playerMode;

    useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);
    // Focus only when explicitly requested (e.g. a click on the Search tile bumps focusToken).
    useEffect(() => { if (focusToken) { inputRef.current?.focus(); setOpen(true); } }, [focusToken]);
    useEffect(() => { if (playerMode) { setOpen(true); inputRef.current?.focus(); } }, [playerMode]);

    const { data: teams = [] } = useQuery({ queryKey: ["teams"], queryFn: getTeams, staleTime: 5 * 60 * 1000 });
    const ql = q.trim().toLowerCase();
    const { data: res } = useQuery({ queryKey: ["gsearch", isNhl ? "nhl" : "pwhl", ql], queryFn: () => (isNhl ? nhlSearch(ql) : search(ql)), enabled: ql.length >= (isNhl ? 1 : 2), staleTime: 30 * 1000 });

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const matchNav = (arr) => arr.filter((n) => n.label.toLowerCase().includes(ql) || (n.kw || "").includes(ql)).slice(0, 6);
    const navHitsAll = (!playerMode && ql.length >= 1) ? matchNav(isNhl ? NHL_NAV : NAV_TARGETS) : [];
    const adminHitsAll = (!playerMode && ql.length >= 1 && hasAdminAccess) ? matchNav(ADMIN_TARGETS) : [];
    const teamHitsAll = (!playerMode && ql.length >= 1) ? (isNhl
        ? (res?.teams || []).map((t) => ({ id: t.abbr, name: t.name, logo_url: t.logo_url, color: t.primary_color, nhl: true }))
        : teams.filter((t) => (t.name || "").toLowerCase().includes(ql) || (t.abbreviation || "").toLowerCase().includes(ql)).slice(0, 8)) : [];
    const playerHitsAll = (res?.players || []);

    // Hard cap of 10 total items across all groups (players → teams → pages → admin).
    let budget = 10;
    const playerHits = playerHitsAll.slice(0, budget); budget -= playerHits.length;
    const teamHits = teamHitsAll.slice(0, budget); budget -= teamHits.length;
    const navHits = navHitsAll.slice(0, budget); budget -= navHits.length;
    const adminHits = adminHitsAll.slice(0, budget); budget -= adminHits.length;

    const hasResults = navHits.length || adminHits.length || teamHits.length || playerHits.length;

    // Single flat row of pills (max 10) for the standardised search nav bar.
    // Colour per result: player/team → their TEAM colour; page/admin → Rosterly purple.
    const ROSTERLY = "#9D4CDD";
    const teamColor = (id) => (TEAM_INFO[id]?.primary) || ROSTERLY;
    const flat = [
        ...playerHits.map((p) => (isNhl
            ? { key: `p-${p.id}`, to: `/nhl/player/${p.id}`, label: p.name, tid: `${testId}-nhl-player-${p.id}`, logo: p.logo_url, color: p.color || ROSTERLY }
            : { key: `p-${p.id}`, to: `/players/${p.id}`, label: p.name, tid: `${testId}-player-${p.id}`, badge: p.team_id, color: teamColor(p.team_id), player: p })),
        ...teamHits.map((t) => (t.nhl
            ? { key: `t-${t.id}`, to: "/nhl/rosters", nhlTeam: t.id, label: t.name, tid: `${testId}-nhl-team-${t.id}`, logo: t.logo_url, color: t.color || ROSTERLY }
            : { key: `t-${t.id}`, to: `/teams/${t.id}`, label: t.name, tid: `${testId}-team-${t.id}`, badge: t.id, color: teamColor(t.id) })),
        ...navHits.map((n) => ({ key: `n-${n.to}`, to: n.to, label: n.label, tid: `${testId}-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`, badge: null, color: ROSTERLY })),
        ...adminHits.map((n) => ({ key: `a-${n.to}`, to: n.to, label: n.label, tid: `${testId}-admin-${n.label.toLowerCase().replace(/\s+/g, "-")}`, badge: null, color: ROSTERLY })),
    ].slice(0, 10);

    const goItem = (it) => { if (it.nhlTeam) setNhlSurfTeam(it.nhlTeam); go(it.to); };
    const badgeOf = (it, fallback = null) => (it.logo !== undefined ? <NhlLogo url={it.logo} /> : it.badge ? <TeamBadge teamId={it.badge} size="xs" asLink={false} /> : fallback);
    const go = (to) => { setQ(""); setOpen(false); onQueryChange?.(false); onNavigate?.(); navigate(to); };
    const pickPlayer = (p) => { pick?.onPick?.(p); setQ(""); setOpen(false); onQueryChange?.(false); onNavigate?.(); };
    const submit = (e) => { e.preventDefault(); if (playerMode) return; if (isNhl) { if (flat[0]) goItem(flat[0]); return; } if (q.trim()) go(`/search?q=${encodeURIComponent(q.trim())}`); };

    const Row = ({ children, onClick, tid }) => (
        <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={tid}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-white hover:bg-white/[0.06] rounded-md transition-colors">
            {children}
        </button>
    );
    const GroupLabel = ({ children }) => (
        <div className="px-3 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-wider text-[#F5C518]/80">{children}</div>
    );

    return (
        <div ref={boxRef} className="relative w-full" data-testid={testId}>
            <form onSubmit={submit} className="relative flex items-stretch gap-1.5">
              <div className="relative flex-1 min-w-0">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748B]" />
                <input
                    ref={inputRef}
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); onQueryChange?.(e.target.value.trim().length >= 1); }}
                    onFocus={() => setOpen(true)}
                    placeholder={playerMode ? "Search a player…" : isNhl ? "Search NHL tabs, teams, players…" : "Search tabs, teams, players…"}
                    data-testid={`${testId}-input`}
                    style={playerMode ? { borderColor: "#9D4CDD", boxShadow: "0 0 0 1px #9D4CDD66" } : undefined}
                    className={`w-full bg-black/30 border ${playerMode ? "border-[#9D4CDD] text-[#E9D5FF] placeholder-[#B388E0]" : "border-white/10 focus:border-white/30 text-white placeholder-[#64748B]"} outline-none rounded-lg transition-colors ${bar ? "py-1 text-xs pl-8 pr-7" : "py-2 text-sm pl-9 pr-8"}`}
                />
                {q && (
                    <button type="button" onClick={() => { setQ(""); setOpen(false); onQueryChange?.(false); }} data-testid={`${testId}-clear`}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-white">
                        <X className="h-4 w-4" />
                    </button>
                )}
              </div>
                {isOwner && <OwnerQuickAdd compact={bar} />}
            </form>

            {open && ql.length >= 1 && (bar ? (
                <div data-testid={`${testId}-results`} className="mt-2 pt-2 border-t border-white/10 flex flex-wrap items-center justify-center gap-1.5">
                    {flat.length === 0 && <span className="text-[10px] text-white/40 italic px-3 py-1.5">{playerMode ? "Type a player name…" : "No matches — press Enter to search all."}</span>}
                    {flat.map((it) => (
                        <button key={it.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => (playerMode && it.player ? pickPlayer(it.player) : goItem(it))} data-testid={it.tid}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black uppercase tracking-wider rounded-lg border bg-black/25 border-white/15 text-[#94A3B8] hover:text-white hover:brightness-125 transition-all">
                            {badgeOf(it)}
                            <span>{it.label}</span>
                        </button>
                    ))}
                </div>
            ) : resultsBar ? (
                <div data-testid={`${testId}-results`}
                    className="fixed left-0 right-0 z-[55] top-[var(--hdr-h,104px)] bg-[#0A0B10]/92 backdrop-blur-xl border-b border-[#1E2233]">
                    <div className="w-full px-4 md:px-8 lg:px-12" data-testid={`${testId}-bar`}>
                        <div className="flex flex-wrap items-center justify-center gap-1.5 py-2">
                            {flat.length === 0 && <span className="text-xs text-white/40 italic px-3 py-1.5">{playerMode ? "Type a player name…" : "No matches — press Enter to search all."}</span>}
                            {flat.map((it) => (
                                <button key={it.key} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => (playerMode && it.player ? pickPlayer(it.player) : goItem(it))} data-testid={it.tid}
                                    className="px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-wider bg-white/10 border border-white/30 text-white hover:bg-white/20 transition-colors flex items-center gap-1.5">
                                    {badgeOf(it, <SearchIcon className="h-3 w-3 text-[#9D4CDD]" />)}
                                    <span>{it.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                <div data-testid={`${testId}-results`}
                    className={`absolute left-0 right-0 z-[60] max-h-[60vh] overflow-y-auto bg-[#0E0F16] border border-white/10 rounded-xl shadow-2xl shadow-black/60 p-1 ${dropUp ? "bottom-full mb-2" : "top-full mt-2"}`}>
                    {!hasResults && <p className="px-3 py-4 text-xs text-white/40 italic text-center">No matches. Press Enter to search all.</p>}
                    {navHits.length > 0 && <><GroupLabel>Pages</GroupLabel>{navHits.map((n) => (
                        <Row key={n.to} onClick={() => go(n.to)} tid={`${testId}-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <SearchIcon className="h-3.5 w-3.5 text-[#9D4CDD]" /><span className="font-bold uppercase tracking-wider text-xs">{n.label}</span>
                        </Row>))}</>}
                    {teamHits.length > 0 && <><GroupLabel>Teams</GroupLabel>{teamHits.map((t) => (
                        <Row key={t.id} onClick={() => (t.nhl ? goItem({ nhlTeam: t.id, to: "/nhl/rosters" }) : go(`/teams/${t.id}`))} tid={`${testId}-team-${t.id}`}>
                            {t.nhl ? <NhlLogo url={t.logo_url} /> : <TeamBadge teamId={t.id} size="xs" asLink={false} />}<span>{t.name}</span>
                        </Row>))}</>}
                    {isNhl && playerHits.length > 0 && <><GroupLabel>Players</GroupLabel>{playerHits.map((p) => (
                        <Row key={p.id} onClick={() => go(`/nhl/player/${p.id}`)} tid={`${testId}-nhl-player-${p.id}`}>
                            <NhlLogo url={p.logo_url} /><span>{p.name}</span>
                            {p.team_name && <span className="text-xs text-[#64748B] ml-auto">{p.team_name}</span>}
                        </Row>))}</>}
                    {!isNhl && playerHits.length > 0 && <><GroupLabel>Players</GroupLabel>{playerHits.map((p) => (
                        <Row key={p.id} onClick={() => (playerMode ? pickPlayer(p) : go(`/players/${p.id}`))} tid={`${testId}-player-${p.id}`}>
                            {p.team_id ? <TeamBadge teamId={p.team_id} size="xs" asLink={false} /> : <span className="h-4 w-4 rounded-sm bg-white/10 shrink-0" />}<span>{p.name}</span>
                            {p.team_name && <span className="text-xs text-[#64748B] ml-auto">{p.team_name}</span>}
                        </Row>))}</>}
                    {adminHits.length > 0 && <><GroupLabel>Admin</GroupLabel>{adminHits.map((n) => (
                        <Row key={n.to} onClick={() => go(n.to)} tid={`${testId}-admin-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                            <span className="h-2 w-2 rounded-full bg-[#9D4CDD] shrink-0" /><span className="font-bold uppercase tracking-wider text-xs">{n.label}</span>
                        </Row>))}</>}
                </div>
            ))}
        </div>
    );
}
